import { useState, useEffect } from "react";
import { Camera, Save } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

// Interface pour les informations du profil
interface UserProfile {
  id: string;
  prenom?: string;
  nom?: string;
  fonction?: string;
  service?: string;
  dateNaissance?: string;
  photo?: string;
}

const EtudiantProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // États pour les données du formulaire
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    fonction: "",
    service: "",
    dateNaissance: "",
  });

  // Récupérer les informations du profil au chargement
  useEffect(() => {
    fetchProfile();
  }, []);

  // Mettre à jour le formData lorsque le profil est chargé
  useEffect(() => {
    if (profile) {
      setFormData({
        prenom: profile.prenom || "",
        nom: profile.nom || "",
        fonction: profile.fonction || "",
        service: profile.service || "",
        dateNaissance: profile.dateNaissance || "",
      });
    }
  }, [profile]);

  // Fonction pour récupérer les informations du profil
  const fetchProfile = async () => {
    try {
      setLoading(true);

      // Pour cet exemple, je vais utiliser les données du localStorage comme point de départ
      const userStr = localStorage.getItem("user");

      if (userStr) {
        const userData = JSON.parse(userStr);
        // Simuler un délai pour montrer le chargement
        setTimeout(() => {
          setProfile({
            id: userData.id || "1",
            prenom: userData.prenom || "",
            nom: userData.nom || "",
            fonction: userData.fonction || "",
            service: userData.service || "",
            dateNaissance: userData.dateNaissance || "",
            photo: userData.photo || "",
          });
          setLoading(false);
        }, 500);
      } else {
        throw new Error("Utilisateur non trouvé");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue");
      setLoading(false);
    }
  };

  // Fonction pour mettre à jour le profil
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);

      // Simuler une mise à jour réussie (à remplacer par un appel API réel)
      setTimeout(() => {
        // Mettre à jour le profil local
        if (profile) {
          const updatedProfile = {
            ...profile,
            prenom: formData.prenom,
            nom: formData.nom,
            fonction: formData.fonction,
            service: formData.service,
            dateNaissance: formData.dateNaissance,
          };

          setProfile(updatedProfile);

          // Mettre à jour le localStorage pour refléter les changements
          const userStr = localStorage.getItem("user");
          if (userStr) {
            const userData = JSON.parse(userStr);
            localStorage.setItem(
              "user",
              JSON.stringify({
                ...userData,
                prenom: formData.prenom,
                nom: formData.nom,
                fonction: formData.fonction,
                service: formData.service,
                dateNaissance: formData.dateNaissance,
              })
            );
          }

          setSuccess("Profil mis à jour avec succès");
        }

        setLoading(false);
      }, 800);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erreur lors de la mise à jour du profil"
      );
      setLoading(false);
    }
  };

  // Gérer les changements dans le formulaire du profil
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-semibold text-gray-800 mb-6">Profil</h1>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded-md mb-4">
                {error}
              </div>
            )}
            {success && (
              <div className="bg-green-100 text-green-700 p-3 rounded-md mb-4">
                {success}
              </div>
            )}

            <div className="mb-6">
              <div className="mb-4 text-center">
                <p className="text-gray-500 mb-2">Photo de profil</p>
                <div className="relative w-24 h-24 mx-auto">
                  <Avatar className="w-24 h-24">
                    <AvatarImage
                      src={profile?.photo || "/images/avatar-placeholder.png"}
                      alt="Photo de profil"
                      className="object-cover"
                    />
                    <AvatarFallback className="bg-green-600 text-white text-2xl">
                      {profile?.prenom?.charAt(0) || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    className="absolute bottom-0 right-0 bg-orange-500 text-white p-1 rounded-full"
                    aria-label="Changer la photo"
                  >
                    <Camera size={16} />
                  </button>
                </div>
              </div>
            </div>

            <form onSubmit={handleProfileUpdate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="prenom"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Prénom
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="nom"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Nom
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="fonction"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Fonction
                  </label>
                  <input
                    type="text"
                    id="fonction"
                    name="fonction"
                    value={formData.fonction}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="service"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Service
                  </label>
                  <input
                    type="text"
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="dateNaissance"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Date de naissance
                  </label>
                  <input
                    type="date"
                    id="dateNaissance"
                    name="dateNaissance"
                    value={formData.dateNaissance}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center justify-center gap-2"
                  disabled={loading}
                >
                  <Save size={18} />
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EtudiantProfile;
